import { defineStore } from 'pinia';
import { computed, ref, shallowRef } from 'vue';
import { ApiError, type Api, type Ok } from '../api/client.ts';
import { buildTree } from '../lib/tree.ts';

export type Twin = Ok<'/twins'>[number];

/**
 * What the operator may see of the estate: the twin tree, and the one setting
 * every status colour depends on.
 *
 * A store rather than a page's own data, because three places read it at once:
 * the navigation tree, every device row that names its twin, and a twin page's
 * child table. Loading it once per page would fetch the same list three times
 * and let the three disagree for as long as the requests take.
 *
 * A setup store, which Pinia recommends and which is plain Composition API:
 * refs are state, computeds are getters, functions are actions. There are no
 * mutations. The one place that idiom survives on purpose is legacy-compat,
 * where Vuex 4 shows what this replaced (ADR-05).
 */
export const useEstateStore = defineStore('estate', () => {
  const twins = shallowRef<Twin[]>([]);
  const silentAfterHours = ref(24);
  const loaded = ref(false);
  const error = ref<ApiError | null>(null);
  /**
   * A twin to highlight in the tree when the route is not a twin page: a
   * device page sets its device's site here, so the navigation still says
   * where the operator is.
   */
  const focus = ref<string | null>(null);

  const byId = computed(() => new Map(twins.value.map((t) => [t.id, t])));
  const tree = computed(() => buildTree(twins.value));

  /**
   * Load the tree and the silence cutoff together. Two requests, one moment:
   * the tile that counts silent devices and the table that colours them must
   * use the same number, and fetching it separately per page is how they end
   * up using two.
   */
  async function load(api: Api): Promise<void> {
    try {
      const [list, overview] = await Promise.all([api.get('/twins'), api.get('/overview')]);
      twins.value = list;
      silentAfterHours.value = overview.silentAfterHours;
      error.value = null;
    } catch (e) {
      error.value = e instanceof ApiError ? e : new ApiError(0, 'internal', String(e));
    } finally {
      loaded.value = true;
    }
  }

  return { twins, silentAfterHours, loaded, error, focus, byId, tree, load };
});
